import { useCallback, useEffect, useState } from 'react';
import { listMyServices } from '../api/services.api';
import type { ServiceGroup } from '../types';

type ServiceMenuState = { owner: string | null; groups: ServiceGroup[]; loaded: boolean };

// One request per signed-in user, shared by every consumer; refreshed on demand.
let cache: ServiceMenuState = { owner: null, groups: [], loaded: false };
let inFlight: { owner: string; promise: Promise<void> } | null = null;
const listeners = new Set<(state: ServiceMenuState) => void>();

function publish(next: ServiceMenuState) {
  cache = next;
  listeners.forEach((listener) => listener(next));
}

function load(owner: string, force = false): Promise<void> {
  if (inFlight?.owner === owner) return inFlight.promise;
  if (!force && cache.owner === owner && cache.loaded) return Promise.resolve();
  const promise = listMyServices()
    .then((groups) => publish({ owner, groups, loaded: true }))
    .catch(() => publish({ owner, groups: [], loaded: true }))
    .finally(() => {
      if (inFlight?.owner === owner) inFlight = null;
    });
  inFlight = { owner, promise };
  return promise;
}

/**
 * Capability-backed service groups for the menu (GET /v1/me/services).
 * `owner` identifies the signed-in user; null (anonymous / signed out) yields no services.
 */
export function useServiceMenu(owner: string | null) {
  const [state, setState] = useState<ServiceMenuState>(cache);

  useEffect(() => {
    listeners.add(setState);
    if (owner) void load(owner);
    return () => {
      listeners.delete(setState);
    };
  }, [owner]);

  const refresh = useCallback(() => (owner ? load(owner, true) : Promise.resolve()), [owner]);
  const visible = owner !== null && state.owner === owner;
  return { groups: visible ? state.groups : [], loaded: visible && state.loaded, refresh };
}
